/**
 * useMediaQuery -- tiny SSR-safe hook around `window.matchMedia`.
 *
 * Pattern is intentionally one source of truth for breakpoints:
 *   * Phones / small tablets  ->  useIsMobile()   = max 767px
 *   * Tablets in landscape    ->  useIsTablet()   = 768-1023px
 *
 * Both mirror Tailwind's default `md`/`lg` cuts, so any class with a
 * `md:` prefix flips at exactly the same boundary these hooks report.
 * Components that already conditionally render based on those hooks
 * should not also rely on JS class toggling; let Tailwind handle the
 * paint and the hook decide whether a mounted listener fires.
 */
import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const get = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(get);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(query);
    const onChange = (evt: MediaQueryListEvent) => setMatches(evt.matches);
    // Some old Safari versions only support addListener/removeListener.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else {
      mql.addListener(onChange);
    }
    // Sync state after mount in case SSR returned a stale value.
    setMatches(mql.matches);
    return () => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', onChange);
      } else {
        mql.removeListener(onChange);
      }
    };
  }, [query]);

  return matches;
}

export const useIsMobile = (): boolean => useMediaQuery('(max-width: 767px)');
export const useIsTablet = (): boolean =>
  useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
